import { observer } from 'mobx-react';
import { comparer, observable, reaction } from 'mobx';
import { withRouter } from 'react-router-dom';
import { v1 as uuidv1 } from 'uuid';

/**
 * Making a component an observer breaks react-router reactivity. These components need to be re-connected with the router.
 */
export const observerWithRouter = (fn: Function) => withRouter(observer(fn));

export class OptimisticObservable<T> {
  networkUpdateRequested: { [string]: Date } = {};
  dataLastModified: Date;

  @observable _updateStack: T[] = [];
  @observable _data: T;
  @observable updateInFlight = false;

  set data(newData: T) {
    this.dataLastModified = new Date();

    this._data = newData;
  }
  get data(): T {
    return this._data;
  }

  constructor(initialValue?: T, networkUpdater: (newItems: T) => Promise<void>) {
    if (initialValue) {
      this.data = initialValue;
    }

    reaction(
      () => {
        if (this._data && typeof this._data.slice === 'function') {
          return this._data.slice();
        }

        return this._data;
      },
      () => {
        const newUpdateStack = [...this._updateStack];
        // Batch changes while previous change is in flight
        if (this.updateInFlight) {
          newUpdateStack[Math.max(this._updateStack.length - 1, 0)] = this._data;
        } else {
          newUpdateStack.push(this._data);
        }

        this._updateStack = newUpdateStack;
      }
    );

    reaction(
      () => ({ updateStack: this._updateStack, updateInFlight: this.updateInFlight }),
      async () => {
        if (this._updateStack.length && !this.updateInFlight) {
          this.updateInFlight = true;
          const updateToProcess = this._updateStack[0];
          this._updateStack.shift();
          await networkUpdater(updateToProcess);
          this.updateInFlight = false;
        }
      }
    );
  }

  markRequested = () => {
    const updateId = uuidv1();
    this.networkUpdateRequested[updateId] = new Date();

    return updateId;
  };

  updateFromRequested = (dataFromResponse: T, updateId: string) => {
    if (
      !comparer.structural(this.data, dataFromResponse) &&
      this.networkUpdateRequested[updateId] &&
      this.networkUpdateRequested[updateId].getTime() > this.dataLastModified.getTime()
    ) {
      this.data = dataFromResponse;

      if (updateId) {
        delete this.networkUpdateRequested[updateId];
      }
    }
  };
}
